/**
 * The account-executive roster behind the configurator's dropdown (CONF-177),
 * and the read/write path the admin page edits it through.
 *
 * Two decisions here are load-bearing:
 *
 * - A quote CANNOT be generated without an account executive
 *   (`validateQuoteField('accountExecutive', …)` in App.js makes it required),
 *   so an empty dropdown is not a degraded experience — it stops every rep
 *   quoting. That is why a successful fetch returning ZERO rows falls back to
 *   the bundled JSON instead of being cached as an answer. This is the
 *   opposite call from `usePriceEditions`, and deliberately so: there, a vendor
 *   with no editions is a real state worth showing honestly; here, an empty
 *   roster only ever means "the table has not been seeded yet", because a
 *   company with no account executives cannot quote at all. It is also what
 *   makes the backend deployable ahead of the seed — the model can ship, the
 *   table can sit empty, and reps keep quoting off the bundled list until an
 *   admin presses Seed.
 *
 * - `status` distinguishes 'ready' (these rows came from the API) from
 *   'fallback' (these rows came from the bundle). The dropdown does not care —
 *   both render. The admin page cares a great deal: editing a roster you only
 *   THINK you fetched would write phantom rows and silently diverge from what
 *   the fleet sees, so the page refuses to edit unless status === 'ready'.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import {
  listAccountExecutives,
  createAccountExecutive,
  updateAccountExecutive,
  deleteAccountExecutive,
} from '../graphql/accountExecOps';
import bundledRoster from '../constants/accountExecs.json';

/** Test-only AEs ride along in dev builds, matching the pre-CONF-177 behaviour
 *  of the two-array JSON file this replaced. Exported because it is only HALF
 *  the policy now: App.js ORs it with adminAccess so an admin generating a
 *  throwaway quote can attribute it to a test name without switching builds
 *  (a build-time env var cannot tell who is signed in; a Cognito group can). */
export const DEV_BUILD_SHOWS_TEST_EXECS = process.env.REACT_APP_BUILD_ENV === 'dev';

/** Email IS the identity (see the schema comment on AccountExecutive), so the
 *  id is derived from it rather than generated. Normalizing here — and only
 *  here — is what makes two admins adding "D. Burke" and "dburke@vdura.com "
 *  collide on the server instead of producing two dropdown entries. */
export function execId(email) {
  return `ae#${String(email ?? '').trim().toLowerCase()}`;
}

/** Surname, which is how the bundled list was ordered and how a rep scans a
 *  dropdown for a colleague. A trailing parenthetical is stripped first so the
 *  test rows ("Mikey Maddocks (Admin)") sort on "Maddocks", not "(Admin)".
 *  Full name breaks ties so the order is total, never render-dependent. */
export function surnameKey(name) {
  const bare = String(name ?? '').replace(/\s*\([^)]*\)\s*$/, '').trim();
  const parts = bare.split(/\s+/);
  return (parts[parts.length - 1] || bare).toLowerCase();
}

function bySurname(a, b) {
  const ka = surnameKey(a.name);
  const kb = surnameKey(b.name);
  if (ka !== kb) return ka < kb ? -1 : 1;
  const na = String(a.name ?? '').toLowerCase();
  const nb = String(b.name ?? '').toLowerCase();
  return na < nb ? -1 : na > nb ? 1 : 0;
}

/** The bundled JSON, in the wire shape the rest of this module speaks, so a
 *  fallback render and a real render are the same code path downstream. */
export function bundledExecutives() {
  const real = (bundledRoster.executives ?? []).map((e) => ({ ...e, isTest: false }));
  const test = (bundledRoster.testExecutives ?? []).map((e) => ({ ...e, isTest: true }));
  return [...real, ...test].map((e) => ({
    id: execId(e.email),
    name: e.name,
    email: e.email,
    isTest: e.isTest,
    createdAt: null,
    createdBy: null,
    updatedBy: null,
  }));
}

/** An AE with no isTest flag is a real AE — absent means false, which is what
 *  every seeded production row looks like. */
const isTestRow = (e) => e.isTest === true;

export function useAccountExecutives({ includeTest = DEV_BUILD_SHOWS_TEST_EXECS } = {}) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('loading');
  const [busy, setBusy] = useState(false);
  // StrictMode double-mounts in dev and an unmounted setState is a warning at
  // best; this also drops the response of a load that a refresh has superseded.
  const live = useRef(true);

  const load = useCallback(async () => {
    try {
      const client = generateClient();
      const res = await client.graphql({
        query: listAccountExecutives,
        variables: { limit: 1000 },
      });
      if (!live.current) return;
      const items = res.data.listAccountExecutives.items ?? [];
      if (items.length === 0) {
        // Not seeded yet — see the module comment. Never an empty dropdown.
        setRows(bundledExecutives());
        setStatus('fallback');
      } else {
        setRows(items);
        setStatus('ready');
      }
    } catch (e) {
      console.error('Account executive fetch failed:', e);
      if (!live.current) return;
      setRows(bundledExecutives());
      setStatus('fallback');
    }
  }, []);

  useEffect(() => {
    live.current = true;
    load();
    return () => { live.current = false; };
  }, [load]);

  /** One write, then a reload. The roster is ~15 rows, so refetching is cheaper
   *  in bugs than reconciling local state against what the server accepted. */
  const mutate = useCallback(async (run) => {
    setBusy(true);
    try {
      const out = await run(generateClient());
      await load();
      return out;
    } finally {
      if (live.current) setBusy(false);
    }
  }, [load]);

  const add = useCallback(({ name, email, isTest = false }, actor) => mutate((client) =>
    client.graphql({
      query: createAccountExecutive,
      variables: {
        input: {
          id: execId(email),
          name: String(name).trim(),
          email: String(email).trim(),
          isTest,
          createdAt: new Date().toISOString(),
          createdBy: actor ?? null,
        },
      },
    })
  ), [mutate]);

  const remove = useCallback((exec) => mutate((client) =>
    client.graphql({
      query: deleteAccountExecutive,
      variables: { input: { id: exec.id } },
    })
  ), [mutate]);

  /**
   * A name/isTest change is an ordinary update. An EMAIL change is not: the id
   * is derived from the email, so the row has to be recreated under its new
   * identity. Create first, delete second — that order means a rejected create
   * (duplicate, or an auth denial) leaves the original untouched, whereas
   * delete-first would lose the AE outright. If the delete then fails the
   * roster carries a visible duplicate, which an admin can clear in one click;
   * that is the strictly better failure of the two.
   */
  const update = useCallback((exec, { name, email, isTest }, actor) => mutate(async (client) => {
    const nextEmail = String(email).trim();
    const nextName = String(name).trim();
    const emailChanged = execId(nextEmail) !== exec.id;

    if (!emailChanged) {
      return client.graphql({
        query: updateAccountExecutive,
        variables: {
          input: { id: exec.id, name: nextName, email: nextEmail, isTest, updatedBy: actor ?? null },
        },
      });
    }

    await client.graphql({
      query: createAccountExecutive,
      variables: {
        input: {
          id: execId(nextEmail),
          name: nextName,
          email: nextEmail,
          isTest,
          createdAt: exec.createdAt ?? new Date().toISOString(),
          createdBy: exec.createdBy ?? null,
          updatedBy: actor ?? null,
        },
      },
    });
    return client.graphql({
      query: deleteAccountExecutive,
      variables: { input: { id: exec.id } },
    });
  }), [mutate]);

  /**
   * Copy the bundled list into the table. Idempotent by construction — ids are
   * derived from email, so a row that already exists fails its create and is
   * counted as skipped rather than duplicating anyone. This is what turns "the
   * model is deployed but empty" into a one-click fix instead of a migration
   * script somebody has to be told about.
   */
  const seed = useCallback((actor) => mutate(async (client) => {
    let created = 0;
    let skipped = 0;
    for (const e of bundledExecutives()) {
      try {
        await client.graphql({
          query: createAccountExecutive,
          variables: {
            input: {
              id: e.id,
              name: e.name,
              email: e.email,
              isTest: e.isTest,
              createdAt: new Date().toISOString(),
              createdBy: actor ?? null,
            },
          },
        });
        created += 1;
      } catch (err) {
        // A conditional-check failure here is the expected, benign case; any
        // other rejection is still worth a console trail before we move on,
        // because a wholly failed seed must not read as "already seeded".
        console.warn(`Seed skipped ${e.email}:`, err?.message ?? err);
        skipped += 1;
      }
    }
    return { created, skipped };
  }), [mutate]);

  // Memoized because App.js holds `executives` across renders and a fresh array
  // identity each time would churn every dependent effect downstream.
  const executives = useMemo(
    () => rows.filter((e) => includeTest || !isTestRow(e)).sort(bySurname),
    [rows, includeTest]
  );
  const allExecutives = useMemo(() => [...rows].sort(bySurname), [rows]);

  return { executives, allExecutives, status, busy, refresh: load, add, update, remove, seed };
}

export default useAccountExecutives;
